/**
 * A tablist of the design system (eg-tabs): the tabs are buttons with
 * role="tab" whose aria-controls names their panel. A click or the arrow
 * keys choose one; the panels of the others get hidden. The class "active"
 * on the chosen tab and its panel stays as the hook of earlier versions.
 *
 * Only the tablists under the given root are bound, so the tabs of the
 * template or of another extension on the same page are left alone.
 */
export class TabStrip {
    static TABLIST_SELECTOR = '[role="tablist"]';
    static TAB_SELECTOR = '[role="tab"]';
    /** the scrolling body of a dialog starts at the top with every change of the panel */
    static SCROLLER_SELECTOR = '.eg-dialog__body';
    /** a dialog keeps one height for all of its tabs, see keepDialogHeight() */
    static DIALOG_SELECTOR = '.eg-dialog';
    static DIALOG_MIN_PROPERTY = '--eg-dialog-min';

    private list: HTMLElement;
    private tabs: HTMLElement[];
    private dialog: HTMLElement | null;
    /** the width of the dialog when its height was measured */
    private measuredWidth: number = -1;

    static bindAll(root: ParentNode): TabStrip[] {
        return Array.from(root.querySelectorAll<HTMLElement>(TabStrip.TABLIST_SELECTOR))
            .map((list) => new TabStrip(list));
    }

    constructor(list: HTMLElement) {
        this.list = list;
        this.tabs = Array.from(list.querySelectorAll<HTMLElement>(TabStrip.TAB_SELECTOR));

        this.tabs.forEach((tab, index) => {
            tab.addEventListener('click', (e) => {
                e.preventDefault();
                this.select(index);
            });
            tab.addEventListener('keydown', (e: KeyboardEvent) => this.onKeydown(e, index));
        });

        this.dialog = list.closest(TabStrip.DIALOG_SELECTOR);
        this.keepDialogHeight();
    }

    /**
     * A dialog with tabs keeps the height its tallest panel needs for every tab, up to the
     * height the viewport allows: the dialog is centred (or a sheet at the bottom of a phone), so
     * until 7.0.0 it shrank and grew with every tab and the tabs jumped away from the pointer.
     * The height is written as --eg-dialog-min on the dialog (components/dialog.less); a shorter
     * panel leaves room below it, but nothing to scroll into. Measured again when the width of the
     * dialog changed, since the texts of the formats wrap differently then.
     */
    keepDialogHeight() {
        const dialog = this.dialog;
        const body = this.list.closest(TabStrip.SCROLLER_SELECTOR) as HTMLElement | null;
        const panels = this.tabs.map((tab) => this.panelOf(tab)).filter((panel) => panel !== null) as HTMLElement[];
        if (!dialog || !body || panels.length < 2) {
            return;
        }

        dialog.style.removeProperty(TabStrip.DIALOG_MIN_PROPERTY);
        this.measuredWidth = dialog.clientWidth;

        // the header and the footer, everything of the dialog but its body
        const outside = dialog.offsetHeight - body.offsetHeight;
        const hidden = panels.map((panel) => panel.hidden);
        let tallest = 0;
        panels.forEach((shown) => {
            panels.forEach((panel) => { panel.hidden = panel !== shown; });
            tallest = Math.max(tallest, body.scrollHeight);
        });
        panels.forEach((panel, i) => { panel.hidden = hidden[i]; });

        if (tallest > 0) {
            dialog.style.setProperty(TabStrip.DIALOG_MIN_PROPERTY, Math.ceil(outside + tallest) + 'px');
        }
    }

    getTabs(): HTMLElement[] {
        return this.tabs;
    }

    /**
     * Shows the panel of the tab at the index and hides the others.
     */
    select(index: number, focus: boolean = false) {
        if (index < 0 || index >= this.tabs.length) {
            return;
        }

        if (this.dialog && this.dialog.clientWidth !== this.measuredWidth) {
            this.keepDialogHeight();
        }

        this.tabs.forEach((tab, i) => {
            const selected = i === index;
            tab.setAttribute('aria-selected', selected ? 'true' : 'false');
            tab.tabIndex = selected ? 0 : -1;
            tab.classList.toggle('active', selected);

            const panel = this.panelOf(tab);
            if (panel) {
                panel.hidden = !selected;
                panel.classList.toggle('active', selected);
            }
        });

        if (focus) {
            this.tabs[index].focus();
        }

        const scroller = this.list.closest(TabStrip.SCROLLER_SELECTOR);
        if (scroller && typeof scroller.scrollTo === 'function') {
            scroller.scrollTo({top: 0});
        }
    }

    private onKeydown(e: KeyboardEvent, index: number) {
        let next: number | null = null;
        const last = this.tabs.length - 1;

        if (e.key === 'ArrowRight') {
            next = index === last ? 0 : index + 1;
        } else if (e.key === 'ArrowLeft') {
            next = index === 0 ? last : index - 1;
        } else if (e.key === 'Home') {
            next = 0;
        } else if (e.key === 'End') {
            next = last;
        }

        if (next === null) {
            return;
        }
        e.preventDefault();
        this.select(next, true);
    }

    private panelOf(tab: HTMLElement): HTMLElement | null {
        const id = tab.getAttribute('aria-controls');
        if (!id) {
            return null;
        }
        return this.list.ownerDocument.getElementById(id);
    }
}
